import { createSlice } from '@reduxjs/toolkit';
import { getOptionAnswerValueById } from '../../../Components/CheckboxInput/checkboxInput.helpers';
import { getComponentValidationStatus } from '../../../Components/__helpers__/component.helpers';
import ValidationStatus from '../../../Data/ValidationStatus';
import PHONE_CONSTRAINT_NAMES from '../../../Data/phoneConstraintNames';
import POSTCODE_CONSTRAINT_NAMES from '../../../Data/postcodeConstraintNames';
import { exposeFormErrorEvent } from '../../../Helpers/events';
import { getComponentValidationMapping } from '../../../Helpers/validation';

export const components = createSlice({
  name: 'COMPONENTS',
  initialState: [], // this comes from preloadedState in the store
  reducers: {
    updateValue(state, action) {
      const { payload } = action;
      const { componentId, value } = payload;
      const component = state[`component_${componentId}`];

      if (!component) {
        return;
      }

      component.input.value = value;
    },

    updateMaskedValue(state, action) {
      const { payload } = action;
      const { componentId, maskedValue } = payload;
      const component = state[`component_${componentId}`];

      if (!component) {
        return;
      }

      component.input.maskedValue = maskedValue;
    },

    updateDefaultValue(state, action) {
      const { payload } = action;
      const { componentId, value } = payload;
      const component = state[`component_${componentId}`];

      if (!component) {
        return;
      }

      component.component.defaultValue = value;
    },

    updateOtherValueAndValue(state, action) {
      const { payload } = action;
      const { componentId, optionId, value } = payload;
      const { options } = state[`component_${componentId}`].input;

      const selectedValuesString = () =>
        state[`component_${componentId}`].input.selectedOptions
          .map((option) => getOptionAnswerValueById(option, options, value))
          .join();

      if (optionId !== 'other-option') {
        return;
      }
      state[`component_${componentId}`].input.value = selectedValuesString();

      // If other-option is unselected value will be empty. We preserve the users input in otherValue
      state[`component_${componentId}`].input.otherValue = value;
    },

    selectOptionAndUpdateValue(state, action) {
      const { payload } = action;
      const { componentId, optionId, selected } = payload;
      const { selectedOptions, options } =
        state[`component_${componentId}`].input;
      const optionIdString = String(optionId);

      const selectedValuesString = () => {
        return state[`component_${componentId}`].input.selectedOptions
          .map((option) =>
            getOptionAnswerValueById(
              option,
              options,
              state[`component_${componentId}`].input.otherValue
            )
          )
          .join();
      };

      if (!selectedOptions) {
        // nothing selected
        state[`component_${componentId}`].input.selectedOptions = [
          optionIdString,
        ];
        state[`component_${componentId}`].input.value = selectedValuesString();
        return;
      }
      if (selectedOptions.includes(optionIdString)) {
        // if the other option has been previously selected and user clicks in the textfield do not remove it
        if (optionIdString === 'other-option' && !!selected) {
          return;
        }
        // if the option has been previously selected remove it
        const optionIndex = selectedOptions.indexOf(optionIdString);
        selectedOptions.splice(optionIndex, 1);
        state[`component_${componentId}`].input.value = selectedValuesString();
        return;
      }
      // default - if the option is not selected add it
      state[`component_${componentId}`].input.selectedOptions.push(
        optionIdString
      );
      state[`component_${componentId}`].input.value = selectedValuesString();
    },

    setSelectedOptions(state, action) {
      const { payload } = action;
      const { componentId, selectedOptions } = payload;

      state[`component_${componentId}`].input.selectedOptions = selectedOptions;
    },

    clearSelectedOptionsAndValue(state, action) {
      const { payload } = action;
      const { componentId } = payload;
      const componentKey = `component_${componentId}`;
      const { input } = state[componentKey];

      if (input && input.selectedOptions) {
        state[`component_${componentId}`].input.selectedOptions = [];
        state[`component_${componentId}`].input.value = '';
      }
    },

    clearConstraintsValidations(state, action) {
      const { payload } = action;
      const { componentId } = payload;

      const component = state[`component_${componentId}`];
      if (!component) {
        return;
      }

      const { constraints } = component.validation;
      if (!constraints) {
        return;
      }

      const constraintType = Object.keys(constraints);

      constraintType.forEach((key) => {
        state[`component_${componentId}`].validation.constraints[key].status =
          ValidationStatus.NOT_VALIDATED;
      });
    },

    setConstraintMessage(state, action) {
      const { payload } = action;
      const { componentId, constraint, message } = payload;

      const component = state[`component_${componentId}`];
      const constraints = component?.validation?.constraints;

      if (!constraints || !constraints[constraint]) {
        return;
      }

      constraints[constraint].message = message;
    },

    setValidationPending(state, action) {
      const { payload } = action;
      const { isPending, componentId } = payload;

      state[`component_${componentId}`].component.validationPendingResponse =
        isPending;
    },

    setPostcodeValidationConstraint(state, action) {
      const { payload } = action;
      const { status, componentId } = payload;

      const component = state[`component_${componentId}`];
      const constraints = component?.validation?.constraints;

      POSTCODE_CONSTRAINT_NAMES.forEach((constraintName) => {
        const constraintToValidate = constraints[constraintName];

        if (constraintToValidate) {
          constraintToValidate.status = status;
        }
      });
    },

    setPhoneValidationConstraint(state, action) {
      const { payload } = action;
      const { status, componentId } = payload;

      const component = state[`component_${componentId}`];
      const constraints = component?.validation?.constraints;

      PHONE_CONSTRAINT_NAMES.forEach((constraintName) => {
        const constraintToValidate = constraints[constraintName];

        if (constraintToValidate) {
          constraintToValidate.status = status;
        }
      });
    },

    setIsCheckedByDefault(state, action) {
      const { payload } = action;
      const { componentId, isCheckedByDefault, optionId } = payload;
      state[`component_${componentId}`].input.options.find(
        (option) => String(option.id) === String(optionId)
      ).is_checked_by_default = isCheckedByDefault;
    },

    setValidationConstraintStatus(state, action) {
      const { payload } = action;
      const { componentId, constraintName, status } = payload;

      const component = state[`component_${componentId}`];
      const constraints = component?.validation?.constraints;

      if (!constraints || !constraints[constraintName]) {
        return;
      }

      constraints[constraintName].status = status;
    },

    /**
     * setComponentValidationStatus
     *
     * This will set any constraints that have not yet been triggered to invalid if the component is invalid it will also trigger the questionAnswered event, else it will trigger the formError event.
     */
    setComponentValidationStatus(state, action) {
      const { payload } = action;
      const { componentId, countryCode, iFrameId, formId } = payload;
      const component = state[`component_${componentId}`];
      /**
       * Here we perform a validation status check / get current status on each constraint.
       */
      const componentValidationMappings = getComponentValidationMapping(
        countryCode,
        component
      );

      /**
       * And then set the status on the component
       */
      Object.keys(componentValidationMappings).forEach((constraint) => {
        const isValid = componentValidationMappings[constraint];
        const constraints = component?.validation?.constraints;

        if (
          isValid &&
          state[`component_${componentId}`].validation.constraints[constraint]
            .status === ValidationStatus.VALID
        ) {
          return;
        }

        if (
          !isValid &&
          state[`component_${componentId}`].validation.constraints[constraint]
            .status === ValidationStatus.INVALID
        ) {
          return;
        }

        if (constraints[constraint]?.status !== ValidationStatus.PENDING) {
          if (isValid) {
            state[`component_${componentId}`].validation.constraints[
              constraint
            ].status = ValidationStatus.VALID;
          } else {
            state[`component_${componentId}`].validation.constraints[
              constraint
            ].status = ValidationStatus.INVALID;
          }
        }
      });

      /**
       * The newly updated status of the component will determine if the questionAnswered or formError event is triggered, but we only want it answered once.
       *
       * The saga will trigger the questionAnswered event if the component is valid, so we only need to trigger the formError event if the component is invalid.
       */
      const componentStatus = getComponentValidationStatus({ component }); // position below update
      if (componentStatus === ValidationStatus.INVALID) {
        exposeFormErrorEvent({
          component,
          iFrameId,
          formId,
          errorObject: {
            message: 'Component validation failed',
            formId,
          },
        });
      }
    },

    setAutoCompleteInputValue(state, action) {
      const { payload } = action;
      const { componentId, value } = payload;
      state[`component_${componentId}`].input.inputValue = value;
    },

    setOptions(state, action) {
      const { payload } = action;
      const { componentId, options } = payload;
      state[`component_${componentId}`].input.options = options;
    },

    setWasSeen(state, action) {
      const { payload } = action;
      const { componentId, value } = payload;
      state[`component_${componentId}`].component.wasSeen = value;
    },

    setBrowserAutoFilled(state, action) {
      const { payload } = action;
      const { componentId, value } = payload;
      state[`component_${componentId}`].component.browserAutoFilled = value;
    },

    setExternalOptions(state, action) {
      const { payload } = action;
      const { componentId, options } = payload;
      state[`component_${componentId}`].input.options = options;
    },

    setBrowserAutoFilledActive(state, action) {
      const { payload } = action;
      const { componentId, value } = payload;
      state[`component_${componentId}`].input.isAutoFilledActive = value;
    },

    setSupplierCardBrandLogoUrl(state, action) {
      const { payload } = action;
      const { componentId, brandLogoUrl } = payload;
      state[
        `component_${componentId}`
      ].component.additionalSettings.brandLogoUrl = brandLogoUrl;
    },

    setSelectAndConnectCardBrandLogoUrl(state, action) {
      const { payload } = action;
      const {
        componentId,
        index,
        brandLogoUrl,
        brandName,
        brandId,
        brandRevisionId,
      } = payload;
      state[`component_${componentId}`].component.additionalSettings.suppliers[
        index
      ].brandLogoUrl = brandLogoUrl;
      state[`component_${componentId}`].component.additionalSettings.suppliers[
        index
      ].brandName = brandName;
      state[`component_${componentId}`].component.additionalSettings.suppliers[
        index
      ].brandId = brandId;
      state[`component_${componentId}`].component.additionalSettings.suppliers[
        index
      ].brandRevisionId = brandRevisionId;
    },

    setCondition(state, action) {
      const { payload } = action;
      const { componentId, condition, value } = payload;
      const component = state[`component_${componentId}`];
      const conditional = component?.component?.conditional || {};
      component.component.conditional = {
        ...conditional,
        [condition]: value,
      };
    },

    setAddressDetails(state, action) {
      const { payload } = action;
      const { componentId, addressDetails } = payload;
      const component = state[`component_${componentId}`];

      if (typeof component?.input !== 'object') {
        return;
      }

      component.input.addressDetails = addressDetails;
    },
  },
});

const { actions, reducer } = components;

export const {
  clearSelectedOptionsAndValue,
  updateValue,
  updateMaskedValue,
  updateDefaultValue,
  selectOptionAndUpdateValue,
  setValidationConstraintStatus,
  clearConstraintsValidations,
  setSelectedOptions,
  setConstraintMessage,
  setPostcodeValidationConstraint,
  setComponentValidationStatus,
  updateOtherValueAndValue,
  setIsCheckedByDefault,
  updateOtherValue,
  setAutoCompleteValue,
  setAutoCompleteInputValue,
  setOptions,
  setPhoneValidationConstraint,
  setWasSeen,
  setBrowserAutoFilled,
  setBrowserAutoFilledActive,
  setExternalOptions,
  setSupplierCardBrandLogoUrl,
  setSelectAndConnectCardBrandLogoUrl,
  setCondition,
  setAddressDetails,
} = actions;

export default reducer;
